import { metricCategory } from '@pulse/metrics';
import { useMemo } from 'react';
import useTemplateMetrics from './useTemplateMetrics';
import { isAppModule, PRODUCT_MODE_MAP } from '@utils/constants';
import { contextTypeAtom } from '@src/atoms/page';
import { useAtomValue } from 'jotai';

function useMetricCategories({
  supportedMetrics,
  selectedMetricList,
  lockedMetricsSet,
  excludeCategories = [],
  allMetrics,
  disableTemplateMetric = false,
}: any) {
  const contextType = useAtomValue(contextTypeAtom);
  const isAppContext = isAppModule || contextType === PRODUCT_MODE_MAP.APP;

  let metricCategories = useMemo(() => {
    const categoryMap: any = {};
    const currentMetricCategory = metricCategory[contextType];
    Object.values(currentMetricCategory).map((category) => (categoryMap[category] = []));

    for (const m in supportedMetrics) {
      const metric = allMetrics[m];
      if (!metric) {
        continue;
      }

      /** As custom metrics are imported, check whether metric exist before use it to avoid error and page crash */
      if (!metric || !('category' in metric)) {
        continue;
      }

      let disabled = lockedMetricsSet.has(metric.name);
      if (isAppContext) {
        disabled =
          disabled ||
          metric.definitionStatus === 'undefined' ||
          metric.definitionStatus === 'in_progress';
      }
      const checked = selectedMetricList.includes(metric.name);

      // a metric can be part of multiple categories
      const categories: any = Array.isArray(metric.category) ? metric.category : [metric.category];
      categories.forEach((category) => {
        categoryMap[category]?.push({
          key: metric.name,
          unit: metric.unit,
          name: `${metric.displayName} ${metric.subscript}`,
          checked,
          disabled,
          isHeavyMetric: metric.isHeavyMetric,
          definitionStatus: metric.definitionStatus,
        });
      });
    }

    // delete excluded categories
    excludeCategories.forEach((category) => {
      delete categoryMap[category];
    });

    // Sorting the options alphabetically for each category
    for (const category in categoryMap) {
      categoryMap[category].sort((a, b) => a.name.localeCompare(b.name));
    }

    return categoryMap;
  }, [supportedMetrics, selectedMetricList, lockedMetricsSet, excludeCategories, allMetrics]);

  // add template metrics for App
  const { templateMetricCategories } = useTemplateMetrics({ selectedMetricList });
  if (isAppContext && !disableTemplateMetric) {
    metricCategories = { ...metricCategories, ...templateMetricCategories };
  }
  return metricCategories;
}

export default useMetricCategories;
